import { Alert, AlertTitle, Typography } from '@mui/material';
import { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import AppSpinner from '../../common/components/AppSpinner';
import EmptyPageAvatar from '../../common/components/EmptyPageAvatar';
import { useGradualUpdate, useScan } from '../../new-scan/actions/scan-actions';
import {
  initArtifacts,
  loadArtifacts,
  loadArtifactsFilters,
  loadArtifactsTopFilters,
  setInitialFilters,
} from '../actions/artifacts-actions';
import { clearStore } from '../store-actions/artifacts-store-actions';
import ArtifactsStore from '../stores/artifacts-store';

const ArtifactsLoader: FC<PropsWithChildren> = ({ children }) => {
  const { artifacts, loading } = useSnapshot(ArtifactsStore);
  const [searchParams] = useSearchParams();
  const filters = searchParams.get('filters');
  const limit = searchParams.get('limit');
  const { isCancelScanLoading } = useScan();

  const { inProgress } = useGradualUpdate(
    useCallback(() => {
      setInitialFilters(filters);
      loadArtifacts({ update: true, cache: false });
      loadArtifactsFilters();
    }, [filters]),
  );

  useEffect(() => {
    if (!filters) initArtifacts();
  }, [filters]);

  useEffect(() => {
    return clearStore;
  }, []);

  useEffect(() => {
    setInitialFilters(filters);
    loadArtifactsTopFilters();
    loadArtifacts({ update: true });
    loadArtifactsFilters();
  }, [filters, limit]);

  if (isCancelScanLoading) {
    return <AppSpinner text='Canceling scan. Please wait...' />;
  }

  if ((loading.isIdle() || loading.isPending()) && artifacts.length === 0) {
    return <AppSpinner text='Loading Artifacts...' />;
  }

  if (inProgress && artifacts.length === 0) {
    return (
      <EmptyPageAvatar
        text={`A scan is now running. The first results will appear soon so don't MOOOOve.`}
      />
    );
  }

  if (loading.isError()) {
    return (
      <Alert severity='error'>
        <AlertTitle>Error</AlertTitle>
        <Typography variant='body1'>An error has occurred</Typography>
      </Alert>
    );
  }
  return <>{children}</>;
};

export default ArtifactsLoader;
